import { useTheme } from '@nivo/core';
import { ResponsiveFunnel } from '@nivo/funnel';
import React, { useContext } from 'react';
import { ThemeContext } from 'styled-components';

import { formatPeopleCount, truncateValue } from '~/utils/helpers';

import { ChartTitle, ChartWrapper } from './AIInsightsUI.styles';
import { FunnelChartBlock } from './blockTypes';
import {
  FUNNEL_PADDING_X as PADDING_X,
  FUNNEL_SIDE_LABEL_MAX_CHARS,
  FUNNEL_SIDE_LABEL_WIDTH as SIDE_LABEL_WIDTH,
} from './chartConfig';

interface Props {
  block: FunnelChartBlock;
}

const Parts = (props: any) => {
  const theme = useContext(ThemeContext);
  return props.parts.map((part: any, index: number) => {
    const x = part.x as number;
    const y = part.y as number;
    const width = part.width as number;
    const height = part.height as number;
    const separatorWidth = props.width - SIDE_LABEL_WIDTH - PADDING_X / 2;

    return (
      <g key={part.data.id} transform={`translate(${x}, ${y})`}>
        <rect
          x={-width / 2}
          y={-height / 2}
          width={width}
          height={height}
          fill={part.color}
          onMouseDown={(e) => part.onMouseDown(e)}
          onMouseMove={(e) => part.onMouseMove(e)}
          onMouseLeave={(e) => part.onMouseLeave(e)}
        />
        {index === 0 && <rect x={-x} y={-y - 4} width={separatorWidth} height={1} fill={theme.colors.neutrals.c050} />}
        <rect x={-x} y={height / 2 + 4} width={separatorWidth} height={1} fill={theme.colors.neutrals.c050} />
      </g>
    );
  });
};

const Labels = (props: any) => {
  const theme = useContext(ThemeContext);
  return props.parts.map((part: any) => {
    if ((part.width as number) < 20) return null;
    return (
      <g key={part.data.id} transform={`translate(${part.x as number}, ${part.y as number})`}>
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fill={theme.colors.neutrals.white}
          fontSize={11}
          fontWeight={600}
          pointerEvents="none"
        >
          {formatPeopleCount(part.data.value as number)}
        </text>
      </g>
    );
  });
};

const SideLabels = (props: any) => {
  const nivoTheme = useTheme();
  return props.parts.map((part: any) => (
    <g key={part.data.id} transform={`translate(-${SIDE_LABEL_WIDTH - 8}, ${part.y as number})`}>
      <text textAnchor="start" dominantBaseline="central" style={nivoTheme.labels.text}>
        {truncateValue((part.data.label as string) ?? '', FUNNEL_SIDE_LABEL_MAX_CHARS)}
      </text>
    </g>
  ));
};

const SimpleFunnelChart = ({ block }: Props) => {
  const theme = useContext(ThemeContext);

  return (
    <div>
      <ChartTitle>{block.title}</ChartTitle>
      <ChartWrapper $height={280}>
        <ResponsiveFunnel
          data={block.data}
          margin={{ top: 8, right: PADDING_X, bottom: 32, left: SIDE_LABEL_WIDTH }} // see docs/ai-insights-chart-rendering.md §6
          beforeSeparatorOffset={64}
          afterSeparatorOffset={64}
          valueFormat={(value: number) => formatPeopleCount(value)}
          spacing={8}
          colors={theme.colors.graph[3]}
          layers={[Parts, Labels, 'annotations', SideLabels]}
        />
      </ChartWrapper>
    </div>
  );
};

export default SimpleFunnelChart;
